import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, BookOpen, Video, FileText, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import LessonBlockRenderer from '../components/LessonBlockRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LessonView = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [blockProgress, setBlockProgress] = useState({ answered: 0, total: 0 });
  const [nav, setNav] = useState({ prev: null, next: null, index: 0, total: 0 });
  const [courseProgress, setCourseProgress] = useState({ completed: 0, total: 0 });

  const isStudent = user?.role === 'Alumno';

  useEffect(() => {
    fetchLesson();
    setStartTime(Date.now());

    // Cleanup: guardar tiempo al salir
    return () => {
      if (isStudent && startTime) {
        saveProgress(false);
      }
    };
  }, [lessonId]);

  const fetchLesson = async () => {
    try {
      const response = await axios.get(`${API_URL}/lessons/${lessonId}`);
      setLesson(response.data);
      fetchCourseMeta(response.data);
    } catch (error) {
      toast.error('Error al cargar la lección');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseMeta = async (lessonData) => {
    const courseId = lessonData?.course?.id;
    if (!courseId) return;
    try {
      const [courseRes, progRes] = await Promise.all([
        axios.get(`${API_URL}/online-courses/${courseId}`),
        axios.get(`${API_URL}/online-courses/${courseId}/my-progress`).catch(() => ({ data: { lessons: {}, quizzes: {} } })),
      ]);
      const modules = (courseRes.data?.modules || []).slice().sort((a, b) => (a.orden || 0) - (b.orden || 0));
      const flat = [];
      modules.forEach((m) => {
        (m.lessons || []).slice().sort((a, b) => (a.orden || 0) - (b.orden || 0)).forEach((l) => flat.push(l));
      });
      // Resolver quizId para lecciones tipo quiz (para navegar y medir progreso)
      const quizLessons = flat.filter((l) => l.tipo === 'quiz');
      const quizResults = await Promise.all(quizLessons.map((l) =>
        axios.get(`${API_URL}/quizzes?lesson_id=${l.id}`).then((r) => ({ id: l.id, quizId: r.data?.[0]?.id })).catch(() => ({ id: l.id, quizId: undefined }))
      ));
      const quizByLesson = {};
      quizResults.forEach((q) => { if (q.quizId) quizByLesson[q.id] = q.quizId; });
      flat.forEach((l) => { if (l.tipo === 'quiz') l.quizId = quizByLesson[l.id]; });

      const idx = flat.findIndex((l) => l.id === lessonId);
      setNav({
        prev: idx > 0 ? flat[idx - 1] : null,
        next: idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null,
        index: idx,
        total: flat.length,
      });
      const lessonsDone = progRes.data?.lessons || {};
      const quizzesPassed = progRes.data?.quizzes || {};
      const completed = flat.filter((l) =>
        l.tipo === 'quiz'
          ? (l.quizId && quizzesPassed[l.quizId]?.passed)
          : lessonsDone[l.id]?.completed
      ).length;
      setCourseProgress({ completed, total: flat.length });
    } catch (e) {
      // non-blocking
    }
  };

  const goToLesson = (l) => {
    if (!l) return;
    if (l.tipo === 'quiz' && l.quizId) {
      navigate(`/quiz/${l.quizId}`);
    } else {
      navigate(`/lessons/${l.id}`);
    }
    window.scrollTo(0, 0);
  };

  const saveProgress = async (completed) => {
    if (!isStudent) return;
    
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    try {
      await axios.post(`${API_URL}/lesson-progress/${lessonId}`, {
        completed: completed,
        tiempo_dedicado: timeSpent
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleComplete = async () => {
    await saveProgress(true);
    toast.success('¡Lección completada!');
    fetchLesson(); // Refresh to show updated progress
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return url;
  };

  const getVimeoEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('player.vimeo.com')) return url;
    const match = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)(?:\/(\w+))?/);
    if (match && match[1]) {
      let hash = match[2];
      if (!hash) { const h = url.match(/[?&]h=(\w+)/); if (h) hash = h[1]; }
      return `https://player.vimeo.com/video/${match[1]}${hash ? `?h=${hash}` : ''}`;
    }
    return url;
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    let u = String(url).trim();
    if (u.includes('<iframe')) {
      const m = u.match(/src=["']([^"']+)["']/i);
      if (m) u = m[1];
    }
    u = u.replace(/&amp;/g, '&');
    if (u.includes('youtube') || u.includes('youtu.be')) {
      return getYouTubeEmbedUrl(u);
    }
    if (u.includes('vimeo')) {
      return getVimeoEmbedUrl(u);
    }
    return u;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!lesson) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Lección no encontrada</p>
        </div>
      </Layout>
    );
  }

  const isCompleted = !!(lesson.progress?.completed || lesson.progress?.completado);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(`/online-courses/${lesson.course?.id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al curso
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            {lesson.duracion_estimada} min
          </div>
        </div>

        {/* Progreso del curso */}
        {nav.total > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid="course-progress-bar">
            <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
              <span className="font-medium">Tema {nav.index + 1} de {nav.total}</span>
              <span>{courseProgress.total > 0 ? Math.round((courseProgress.completed / courseProgress.total) * 100) : 0}% del curso completado</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#004E89] transition-all duration-500"
                style={{ width: `${courseProgress.total > 0 ? (courseProgress.completed / courseProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Lesson Header */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">
                {lesson.module?.nombre}
              </p>
              <h1 className="font-manrope font-bold text-2xl text-slate-900">
                {lesson.nombre}
              </h1>
            </div>
            {isCompleted && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Completada</span>
              </div>
            )}
          </div>
        </div>

        {/* Lesson Content */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {/* Blocks / Tema interactivo */}
          {lesson.tipo === 'blocks' && (
            <div className="p-6">
              {lesson.bloques && lesson.bloques.length > 0 ? (
                <LessonBlockRenderer blocks={lesson.bloques} onProgress={setBlockProgress} />
              ) : (
                <p className="text-center text-slate-400 py-8">Este tema aún no tiene contenido.</p>
              )}
            </div>
          )}

          {/* Video Content */}
          {lesson.tipo === 'video' && lesson.video_url && (
            <div className="aspect-video bg-black">
              <iframe
                src={getEmbedUrl(lesson.video_url)}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )}

          {/* Text Content */}
          {lesson.tipo === 'text' && lesson.contenido && (
            <div className="p-6">
              <div 
                className="prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: lesson.contenido }}
                style={{
                  '--tw-prose-headings': '#1e293b',
                  '--tw-prose-body': '#475569',
                }}
              />
              <style>{`
                .prose h1 { font-size: 1.875rem; font-weight: 700; margin-bottom: 1rem; color: #1e293b; }
                .prose h2 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem; color: #1e293b; }
                .prose h3 { font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem; color: #1e293b; }
                .prose p { margin-bottom: 1rem; line-height: 1.75; color: #475569; }
                .prose ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
                .prose ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
                .prose li { margin-bottom: 0.5rem; color: #475569; }
                .prose img { max-width: 100%; height: auto; margin: 1rem 0; border-radius: 0.5rem; }
                .prose table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
                .prose th, .prose td { border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; }
                .prose th { background-color: #f8fafc; font-weight: 600; }
                .prose blockquote { border-left: 4px solid #e2e8f0; padding-left: 1rem; margin: 1rem 0; color: #64748b; font-style: italic; }
                .prose code { background-color: #f1f5f9; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-family: monospace; font-size: 0.875rem; }
                .prose pre { background-color: #1e293b; color: #e2e8f0; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; }
                .prose pre code { background-color: transparent; padding: 0; }
                .prose mark { background-color: #fef3c7; padding: 0.125rem 0.25rem; border-radius: 0.125rem; }
              `}</style>
            </div>
          )}

          {/* Document Content */}
          {lesson.tipo === 'document' && lesson.documento_url && (
            <div className="p-6">
              <div className="bg-slate-50 rounded-lg p-8 text-center">
                <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">Documento disponible</h3>
                <p className="text-slate-500 mb-4">Haz clic en el botón para ver o descargar el documento</p>
                <Button asChild className="bg-[#004E89] hover:bg-[#003B66]">
                  <a href={lesson.documento_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir documento
                  </a>
                </Button>
              </div>
            </div>
          )}

          {/* Quiz placeholder */}
          {lesson.tipo === 'quiz' && (
            <div className="p-6">
              <div className="bg-yellow-50 rounded-lg p-8 text-center">
                <BookOpen className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">Evaluación</h3>
                <p className="text-slate-500">El sistema de evaluaciones estará disponible próximamente</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {isStudent && (() => {
          const pendingQuestions = lesson.tipo === 'blocks' && blockProgress.total > 0 && blockProgress.answered < blockProgress.total;
          return (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-medium text-slate-900">¿Has terminado esta lección?</h3>
                <p className="text-sm text-slate-500">
                  {pendingQuestions
                    ? `Responde todas las preguntas del tema para completarlo (${blockProgress.answered}/${blockProgress.total} respondidas)`
                    : 'Marca como completada para continuar con tu progreso'}
                </p>
              </div>
              <Button
                onClick={handleComplete}
                disabled={isCompleted || pendingQuestions}
                className={isCompleted ? 'bg-green-600' : 'bg-[#004E89] hover:bg-[#003B66]'}
                data-testid="mark-complete-btn"
              >
                {isCompleted ? (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Completada</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Marcar como completada</>
                )}
              </Button>
            </div>
          </div>
          );
        })()}

        {/* Time spent indicator */}
        {lesson.progress?.tiempo_dedicado > 0 && (
          <div className="text-center text-sm text-slate-500">
            Tiempo dedicado: {Math.floor(lesson.progress.tiempo_dedicado / 60)} min {lesson.progress.tiempo_dedicado % 60} seg
          </div>
        )}

        {/* Navegación entre temas */}
        {nav.total > 0 && (
          <div className="flex items-center justify-between gap-3 pt-2" data-testid="lesson-nav">
            <Button
              variant="outline"
              onClick={() => goToLesson(nav.prev)}
              disabled={!nav.prev}
              className="border-slate-300"
              data-testid="prev-lesson-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Tema anterior
            </Button>
            {nav.next ? (
              <Button
                onClick={() => goToLesson(nav.next)}
                className="bg-[#004E89] hover:bg-[#003B66]"
                data-testid="next-lesson-btn"
              >
                Siguiente tema <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate(`/online-courses/${lesson.course?.id}`)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="finish-course-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Finalizar y volver al curso
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LessonView;
